import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter, take } from 'rxjs/operators';
import { driver } from 'driver.js';

import { TourId, TourStep, TourContext, TourDefinition } from './tour.types';
import { TOUR_DEFINITIONS_MAP } from './tour.definitions';
import { TourFlagService } from './tour-flag.service';
import { AuthService } from 'src/app/core/services/auth.service';
import { camelToSnake } from 'src/app/core/utils/string.utils';

@Injectable({ providedIn: 'root' })
export class TourService {
  private driverInstance: ReturnType<typeof driver> | null = null;
  private currentStepIndex = 0;
  private activeSteps: TourStep[] = [];
  private activeTourId: TourId | null = null;
  private activeContext: TourContext | null = null;
  private pendingStart = false;
  private navegando = false;
  private proxyEl: HTMLElement | null = null;
  private ringEl: HTMLElement | null = null;
  private drawerAbertoPeloTour = false;

  constructor(
    private router: Router,
    private authService: AuthService,
    private tourFlagService: TourFlagService
  ) {}

  // ─── API pública ───────────────────────────────────────────────────────────

  /**
   * Inicia um tour específico, montando os steps conforme o contexto fornecido.
   * Não verifica flags — inicia incondicionalmente (ex.: ao clicar em "Rever tour").
   */
  iniciarTour(tourId: TourId, context: TourContext = {}): void {
    const definicao = this.getTourDefinition(tourId);
    if (!definicao) {
      console.warn(`[TourService] Tour não encontrado: ${tourId}`);
      return;
    }

    this.activeTourId = tourId;
    this.activeContext = context;
    this.activeSteps = this.resolverSteps(definicao, context);
    this.navegando = false;
    this.currentStepIndex = 0;
    this.executarStep(0);
  }

  /**
   * Verifica se o tour deve ser iniciado com base nas flags e contexto.
   * Usado pelos componentes em ngOnInit.
   *
   * @returns true se o tour foi iniciado, false caso contrário
   */
  verificarEDisparar(tourId: TourId, context: TourContext = {}): boolean {
    if (!this.deveDisparar(tourId, context)) return false;
    this.iniciarTour(tourId, context);
    return true;
  }

  /**
   * Encerra o tour ativo e persiste a flag de conclusão no backend.
   * Quando completou === false (usuário fechou/pulou), a flag ainda é marcada.
   */
  encerrarTour(tourId: TourId, completou: boolean): void {
    this.destruirInstanciaAtual();

    const definicao = this.getTourDefinition(tourId);
    if (!definicao) return;

    const flagCamel = definicao.flagConclusao as string;
    const flagSnake = camelToSnake(flagCamel);

    if (!this.tourFlagService.isConcluido(definicao.flagConclusao)) {
      this.tourFlagService.marcarConcluido(flagSnake).subscribe({
        error: (err) => console.error('[TourService] Erro ao persistir flag de conclusão', err),
      });
    }

    // Ações pós-conclusão específicas de cada tour
    if (tourId === 'tour-home' && completou) {
      this.tourFlagService.marcarFlag('primeiro_login', false).subscribe();
      this.tourFlagService.marcarFlag('menu_org_pulsando', true).subscribe();
    }

    // tour-colaboradores tem uma 2ª passagem "com dados" — quando o tour é
    // encerrado com temDados=true, precisamos marcar também essa flag específica,
    // senão deveDisparar continua retornando true a cada visita (ver linha ~126).
    if (
      tourId === 'tour-colaboradores'
      && this.activeContext?.temDados === true
      && !this.tourFlagService.isConcluido('tourColaboradoresComDadosConcluido')
    ) {
      const flagComDadosSnake = camelToSnake('tourColaboradoresComDadosConcluido');
      this.tourFlagService.marcarConcluido(flagComDadosSnake).subscribe({
        error: (err) => console.error(
          '[TourService] Erro ao persistir flag tour_colaboradores_com_dados_concluido',
          err
        ),
      });
    }

    this.fecharDrawerMobile();
    this.activeTourId = null;
    this.activeContext = null;
    this.activeSteps = [];
  }

  /**
   * Retorna a definição de um tour pelo id.
   */
  getTourDefinition(tourId: string): TourDefinition | undefined {
    return TOUR_DEFINITIONS_MAP[tourId];
  }

  /**
   * Avalia se um tour deve ser disparado automaticamente com base nas flags do usuário
   * e no contexto de dados da página.
   */
  deveDisparar(tourId: TourId, context: TourContext = {}): boolean {
    const flags = this.tourFlagService.getFlags();

    // Sem flags carregadas ainda: não disparar (evita flash desnecessário)
    if (!flags) return false;

    switch (tourId) {
      case 'tour-home':
        return flags.primeiroLogin === true;

      case 'tour-org-estrutura':
        return !flags.tourOrgEstruturaConcluido;

      case 'tour-org-times':
        return !flags.tourOrgTimesConcluido;

      case 'tour-org-hierarquia':
        return !flags.tourOrgHierarquiaConcluido;

      case 'tour-colaboradores':
        // Primeira passagem: tour principal ainda não concluído
        if (!flags.tourColaboradoresConcluido) return true;
        // Segunda passagem: já há dados reais e tour "com dados" ainda não concluído
        if (!flags.tourColaboradoresComDadosConcluido && context.temDados) return true;
        return false;

      case 'tour-colaborador-detalhe':
        // Só dispara se há dados de atividade para o colaborador
        return !flags.tourColaboradorDetalheConcluido && !!context.temDados;

      case 'tour-relatorios':
        return !flags.tourRelatoriosConcluido;

      case 'tour-perfil':
        return !flags.tourPerfilConcluido;

      default:
        return false;
    }
  }

  // ─── Compatibilidade com código legado ────────────────────────────────────

  /**
   * @deprecated Use iniciarTour('tour-home') diretamente.
   * Mantido para compatibilidade com dashboard.component.ts existente.
   */
  iniciar(): void {
    this.iniciarTour('tour-home');
  }

  /** @deprecated Use verificarEDisparar() */
  agendarInicio(): void {
    this.pendingStart = true;
  }

  /** @deprecated Use verificarEDisparar() */
  deveIniciar(): boolean {
    if (this.pendingStart) {
      this.pendingStart = false;
      return true;
    }
    return false;
  }

  /** @deprecated Use tourFlagService.isConcluido('tourHomeConcluido') */
  tourJaConcluido(): boolean {
    return !!this.authService.getUser()?.tourConcluido;
  }

  // ─── Resolução de steps por contexto ──────────────────────────────────────

  private resolverSteps(definicao: TourDefinition, context: TourContext): TourStep[] {
    const id = definicao.id;
    const v = definicao.variantes;

    if (!v) return [...definicao.steps];

    switch (id) {
      case 'tour-org-hierarquia': {
        const steps = [...definicao.steps];
        if (!context.ehAdmin) {
          // Remove steps 2 e 3 (índices 1 e 2) que são exclusivos de admin
          return [steps[0], steps[3]].filter(Boolean);
        }
        return steps;
      }

      case 'tour-org-times':
        return context.temTimes ? (v.comTimes ?? definicao.steps) : (v.semTimes ?? definicao.steps);

      case 'tour-colaboradores': {
        const base = [...definicao.steps]; // step 1 sempre presente
        if (!context.temTimes) {
          return [...base, ...(v.semTimes ?? [])];
        }
        if (!context.temDados) {
          return [...base, ...(v.comTimes ?? []), ...(v.semDados ?? [])];
        }
        // Com dados reais — inclui steps 4 e 5
        return [...base, ...(v.comTimes ?? []), ...(v.comDados ?? [])];
      }

      case 'tour-relatorios':
        if (context.temDados) {
          return [...definicao.steps, ...(v.comDados ?? [])];
        }
        return [...definicao.steps];

      default:
        return [...definicao.steps];
    }
  }

  // ─── Execução de steps (lógica de driver.js — intacta) ────────────────────

  private executarStep(index: number): void {
    if (index >= this.activeSteps.length) {
      if (this.activeTourId) {
        this.encerrarTour(this.activeTourId, true);
      }
      return;
    }

    const step = this.activeSteps[index];
    const currentPath = this.router.url.split('?')[0];
    const jaEstaNaRota = currentPath === step.route;
    const mesmaQueryParams = this.queryParamsIguais(step.queryParams);
    const isMenuStep = step.element.startsWith('[data-tour="menu-');

    if (jaEstaNaRota && mesmaQueryParams) {
      this.agendarDestaque(step, index, 100, isMenuStep);
    } else {
      this.router.events
        .pipe(filter(e => e instanceof NavigationEnd), take(1))
        .subscribe(() => this.agendarDestaque(step, index, 500, isMenuStep));
      this.router.navigate([step.route], { queryParams: step.queryParams });
    }
  }

  private agendarDestaque(
    step: TourStep,
    index: number,
    baseDelay: number,
    isMenuStep: boolean
  ): void {
    // No mobile, abrir o drawer antes de destacar steps do menu
    if (isMenuStep && this.isMobileViewport()) {
      this.abrirDrawerMobile().then(() => {
        setTimeout(() => this.destacarElemento(step, index), baseDelay);
      });
      return;
    }

    // No mobile, fechar drawer se estava aberto pelo tour e o step não é do menu
    if (!isMenuStep && this.drawerAbertoPeloTour) {
      this.fecharDrawerMobile();
    }

    if (!isMenuStep) {
      setTimeout(() => this.destacarElemento(step, index), baseDelay);
      return;
    }

    // Desktop: colapsar menu expandido antes de destacar
    const navShell = document.querySelector('.nav-shell');
    if (navShell?.classList.contains('is-expanded')) {
      navShell.dispatchEvent(new MouseEvent('mouseleave', { bubbles: true }));
      let disparado = false;
      const disparar = (): void => {
        if (disparado) return;
        disparado = true;
        navShell.removeEventListener('transitionend', onTransitionEnd);
        setTimeout(() => this.destacarElemento(step, index), baseDelay);
      };
      const onTransitionEnd = (e: Event): void => {
        if ((e as TransitionEvent).propertyName === 'width') disparar();
      };
      navShell.addEventListener('transitionend', onTransitionEnd);
      setTimeout(disparar, 800);
    } else {
      setTimeout(() => this.destacarElemento(step, index), baseDelay);
    }
  }

  private isMobileViewport(): boolean {
    return window.innerWidth <= 960;
  }

  private abrirDrawerMobile(): Promise<void> {
    return new Promise(resolve => {
      const sider = document.querySelector('.sider');
      if (!sider?.classList.contains('mobile-open')) {
        const hamburger = document.querySelector('.mobile-hamburger') as HTMLElement;
        if (hamburger) {
          hamburger.click();
          this.drawerAbertoPeloTour = true;
          setTimeout(resolve, 400);
          return;
        }
      }
      resolve();
    });
  }

  private fecharDrawerMobile(): void {
    if (!this.drawerAbertoPeloTour) return;
    const sider = document.querySelector('.sider');
    if (sider?.classList.contains('mobile-open')) {
      const backdrop = document.querySelector('.mobile-backdrop') as HTMLElement;
      if (backdrop) backdrop.click();
    }
    this.drawerAbertoPeloTour = false;
  }

  private queryParamsIguais(queryParams?: Record<string, string>): boolean {
    if (!queryParams) return true;
    const current = this.router.parseUrl(this.router.url).queryParams as Record<string, string>;
    return Object.keys(queryParams).every(k => current[k] === queryParams[k]);
  }

  private destruirInstanciaAtual(): void {
    this.removerProxy();
    document.querySelector('.nav-shell')?.classList.remove('tour-nav-boost');
    if (this.driverInstance) {
      this.navegando = true;
      this.driverInstance.destroy();
      this.driverInstance = null;
      this.navegando = false;
    }
  }

  private criarProxy(rect: DOMRect): HTMLElement {
    const proxy = document.createElement('div');
    proxy.style.cssText = `position:fixed;top:${rect.top}px;left:${rect.left}px;width:${rect.width}px;height:${rect.height}px;pointer-events:none;z-index:0;`;
    document.body.appendChild(proxy);
    this.proxyEl = proxy;

    const ring = document.createElement('div');
    ring.style.cssText = [
      `position:fixed`,
      `top:${rect.top}px`,
      `left:${rect.left}px`,
      `width:${rect.width}px`,
      `height:${rect.height}px`,
      `pointer-events:none`,
      `z-index:100003`,
      `border-radius:14px`,
      `border:2px solid rgba(255,255,255,0.70)`,
      `box-shadow:0 0 4px rgba(255,255,255,0.25)`,
    ].join(';');
    document.body.appendChild(ring);
    this.ringEl = ring;

    return proxy;
  }

  private removerProxy(): void {
    this.ringEl?.remove();
    this.ringEl = null;
    this.proxyEl?.remove();
    this.proxyEl = null;
  }

  private destacarElemento(step: TourStep, index: number): void {
    this.destruirInstanciaAtual();

    const el = document.querySelector(step.element) as HTMLElement | null;
    if (!el) {
      this.currentStepIndex++;
      this.executarStep(this.currentStepIndex);
      return;
    }

    const isMenuStep = step.element.startsWith('[data-tour="menu-');
    const targetEl: HTMLElement | string = isMenuStep
      ? this.criarProxy(el.getBoundingClientRect())
      : el;

    if (isMenuStep) {
      document.querySelector('.nav-shell')?.classList.add('tour-nav-boost');
    }

    const totalSteps = this.activeSteps.length;
    const isFirst = index === 0;
    const isLast = index === totalSteps - 1;
    const progressText = `Passo ${index + 1} de ${totalSteps}`;
    const showButtons: ('next' | 'previous' | 'close')[] = isFirst
      ? ['next', 'close']
      : ['next', 'previous', 'close'];

    this.driverInstance = driver({
      allowClose: true,
      animate: false,
      overlayColor: 'rgba(15, 23, 42, 0.72)',
      smoothScroll: true,
      stagePadding: 10,
      stageRadius: 12,
      popoverClass: isMenuStep
        ? 'manager-tour-popover manager-tour-popover--menu'
        : 'manager-tour-popover',
      nextBtnText: isLast ? 'Concluir 🎉' : 'Próximo →',
      prevBtnText: '← Anterior',
      onPopoverRender: (popoverObj: { nextButton: HTMLElement; previousButton: HTMLElement; closeButton: HTMLElement }) => {
        const set = (el: HTMLElement, prop: string, val: string): void =>
          el.style.setProperty(prop, val, 'important');

        const next = popoverObj.nextButton;
        const prev = popoverObj.previousButton;
        const close = popoverObj.closeButton;

        for (const btn of [next, prev, close]) {
          set(btn, 'outline', 'none');
          set(btn, 'box-shadow', 'none');
        }

        set(next, 'background', '#2563eb');
        set(next, 'border', '1px solid transparent');
        set(next, 'color', '#ffffff');
        set(next, 'border-radius', '10px');
        set(next, 'padding', '7px 16px');
        set(next, 'font-size', '13px');
        set(next, 'font-weight', '600');
        set(next, 'font-family', "'Inter', sans-serif");
        set(next, 'cursor', 'pointer');
        set(next, 'text-shadow', 'none');

        set(prev, 'background', 'transparent');
        set(prev, 'border', '1px solid #e5e7eb');
        set(prev, 'color', '#374151');
        set(prev, 'border-radius', '10px');
        set(prev, 'padding', '7px 14px');
        set(prev, 'font-size', '13px');
        set(prev, 'font-weight', '500');
        set(prev, 'font-family', "'Inter', sans-serif");
        set(prev, 'cursor', 'pointer');
        set(prev, 'text-shadow', 'none');
      },
      onNextClick: () => {
        this.destruirInstanciaAtual();
        this.currentStepIndex++;
        this.executarStep(this.currentStepIndex);
      },
      onPrevClick: () => {
        this.destruirInstanciaAtual();
        this.currentStepIndex--;
        this.executarStep(this.currentStepIndex);
      },
      onDestroyStarted: () => {
        if (!this.navegando) {
          if (this.activeTourId) {
            this.encerrarTour(this.activeTourId, false);
          }
        }
      },
    });

    this.driverInstance.highlight({
      element: targetEl as unknown as string,
      popover: {
        title: step.title,
        description: `${step.description}<br><small class="tour-progress">${progressText}</small>`,
        side: step.side,
        align: 'center',
        showButtons,
        onCloseClick: () => {
          if (this.activeTourId) {
            this.encerrarTour(this.activeTourId, false);
          }
        },
      },
    });
  }

}
